"use client";

import { useState, useRef, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: string;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}

export function Tooltip({ content, children, side = "top" }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!visible || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 6;
    let x = rect.left + rect.width / 2;
    let y = 0;

    if (side === "top") y = rect.top - gap;
    else if (side === "bottom") y = rect.bottom + gap;
    else if (side === "left") { x = rect.left - gap; y = rect.top + rect.height / 2; }
    else { x = rect.right + gap; y = rect.top + rect.height / 2; }

    setCoords({ x, y });
  }, [visible, side]);

  const positionClass =
    side === "top" ? "-translate-x-1/2 -translate-y-full" :
    side === "bottom" ? "-translate-x-1/2" :
    side === "left" ? "-translate-x-full -translate-y-1/2" :
    "-translate-y-1/2";

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        className="inline-flex"
      >
        {children}
      </span>
      {visible && typeof window !== "undefined" && createPortal(
        <div
          role="tooltip"
          style={{ left: coords.x, top: coords.y }}
          className={`pointer-events-none fixed z-50 max-w-xs rounded-md bg-foreground px-2 py-1 text-xs text-background shadow-md ${positionClass}`}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}
